/**
 * useDesktopView - Hook for detecting desktop screen sizes and managing responsive state
 *
 * Breakpoints:
 * - Desktop: >= 1024px (two-column layout)
 * - Large Desktop: >= 1440px (three-column with right panel)
 * - 4K: >= 2560px (scaled up for high-res displays)
 */

import { useState, useEffect, useCallback } from 'react';

interface DesktopViewState {
  isDesktop: boolean;
  isLargeDesktop: boolean;
  is4K: boolean;
  showRightPanel: boolean;
  screenWidth: number;
}

const BREAKPOINTS = {
  desktop: 1024,
  desktopLg: 1440,
  desktop4k: 2560,
} as const;

export const useDesktopView = (): DesktopViewState => {
  const [state, setState] = useState<DesktopViewState>(() => {
    // SSR-safe initial state
    if (typeof window === 'undefined') {
      return {
        isDesktop: false,
        isLargeDesktop: false,
        is4K: false,
        showRightPanel: false,
        screenWidth: 0,
      };
    }

    const width = window.innerWidth;
    return {
      isDesktop: width >= BREAKPOINTS.desktop,
      isLargeDesktop: width >= BREAKPOINTS.desktopLg,
      is4K: width >= BREAKPOINTS.desktop4k,
      showRightPanel: width >= BREAKPOINTS.desktopLg,
      screenWidth: width,
    };
  });

  const updateState = useCallback(() => {
    const width = window.innerWidth;
    setState({
      isDesktop: width >= BREAKPOINTS.desktop,
      isLargeDesktop: width >= BREAKPOINTS.desktopLg,
      is4K: width >= BREAKPOINTS.desktop4k,
      showRightPanel: width >= BREAKPOINTS.desktopLg,
      screenWidth: width,
    });
  }, []);

  useEffect(() => {
    // Update on mount
    updateState();

    // Debounced resize handler
    let timeoutId: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateState, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [updateState]);

  return state;
};

export default useDesktopView;
